/**
 * AI Data Highlight (#10) — surface at-risk rows in ANY list, driven from the registered
 * semantic-layer measures (core 0019). A "flag" is a count measure whose filter isolates a
 * problem set (overdue invoices, urgent cases, high-risk KYC, past-due subscriptions…). We
 * evaluate that SQL filter client-side against each loaded row and mark the matches — no
 * per-surface config, no server round-trip: the same machinery lights up every ListView.
 */
import type { Measure } from '@solum/sdk'
import type { Row } from '@solum/sdk'

// A measure counts as a risk FLAG if it's a filtered count whose name signals risk.
const RISK_VOCAB = /overdue|urgent|high[ _-]?risk|at[ _-]?risk|past[ _-]?due|breach|expir|stale|blocked|over[_-]|escalat|dunning|churn|failing/i
export function riskMeasures(measures: Measure[] | undefined): Measure[] {
  return (measures ?? []).filter((m) =>
    m.aggregation === 'count' && !!m.filter && (RISK_VOCAB.test(m.key) || RISK_VOCAB.test(m.label)))
}

const unquote = (s: string) => s.trim().replace(/^'(.*)'$/, '$1')

/** Evaluate one atom (`col op literal`, `col in (...)`, `col is [not] null`) against a row. */
function evalAtom(atom: string, row: Row): boolean {
  const a = atom.trim()
  let m: RegExpMatchArray | null
  if ((m = a.match(/^(\w+)\s+is\s+not\s+null$/i))) return row[m[1]!] != null
  if ((m = a.match(/^(\w+)\s+is\s+null$/i))) return row[m[1]!] == null
  if ((m = a.match(/^(\w+)\s+in\s*\((.+)\)$/i))) {
    const vals = m[2]!.split(',').map(unquote)
    return vals.includes(String(row[m[1]!]))
  }
  if ((m = a.match(/^(\w+)\s*(>=|<=|<>|!=|=|>|<)\s*(.+)$/))) {
    const [, col, op, rawLit] = m as unknown as [string, string, string, string]
    const cell = row[col]
    if (cell == null) return false
    const lit = unquote(rawLit)
    const numeric = /^-?\d/.test(lit) && !Number.isNaN(Number(lit))
    const x: number | string = numeric ? Number(cell) : String(cell)
    const y: number | string = numeric ? Number(lit) : lit
    switch (op) {
      case '=': return x === y
      case '<>': case '!=': return x !== y
      case '>': return x > y
      case '<': return x < y
      case '>=': return x >= y
      case '<=': return x <= y
    }
  }
  return false
}

/** A registered measure filter (OR of ANDs — the shape every seeded measure uses). */
export function matchFilter(filter: string | null | undefined, row: Row): boolean {
  if (!filter) return false
  return filter.split(/\s+or\s+/i).some((clause) =>
    clause.split(/\s+and\s+/i).every((atom) => evalAtom(atom, row)))
}

/** The flags a row trips, given the entity's risk measures. Empty = healthy. */
export function rowFlags(row: Row, risk: Measure[]): Measure[] {
  return risk.filter((m) => matchFilter(m.filter, row))
}
